import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, Save, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Card, EmptyState } from "@/components/ranger/PageHeader";
import { WarnDialog } from "@/components/ranger/WarnDialog";
import { BTN_PRIMARY } from "@/pages/Servers";
import { api, errMsg, fmtTime } from "@/lib/api";

const WatchSettings = ({ cid, canEdit }) => {
  const [cfg, setCfg] = useState(null);
  const [kw, setKw] = useState("");
  useEffect(() => { api.get(`/communities/${cid}/moderation-config`).then((r) => { setCfg(r.data); setKw(r.data.raid_keywords.join(", ")); }); }, [cid]);
  if (!cfg) return null;
  const save = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.put(`/communities/${cid}/moderation-config`, { rule_watch_enabled: cfg.rule_watch_enabled, raid_keywords: kw.split(",").map((s) => s.trim()).filter(Boolean) });
      setCfg(data); setKw(data.raid_keywords.join(", ")); toast.success("Rule watch saved");
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <form onSubmit={save} className="mb-5 flex flex-wrap items-end gap-4 border-b border-[#1E2D4A] pb-5">
      <label className="flex items-center gap-3 text-sm text-slate-300"><Switch data-testid="rulewatch-switch" disabled={!canEdit} checked={cfg.rule_watch_enabled} onCheckedChange={(v) => setCfg({ ...cfg, rule_watch_enabled: v })} />Watch log during PvE</label>
      <label className="min-w-[260px] flex-1 space-y-1.5"><span className="rg-label block">Raid keywords (comma separated)</span>
        <Input data-testid="rulewatch-keywords-input" disabled={!canEdit} value={kw} onChange={(e) => setKw(e.target.value)} placeholder="e.g. destroyed, explosive" className="border-[#1E2D4A] bg-[#050811] font-mono" /></label>
      {canEdit && <button data-testid="rulewatch-save-button" className={BTN_PRIMARY}><Save className="h-4 w-4" />Save</button>}
      <p className="basis-full text-xs text-slate-500">Player kills are detected from standard "X was killed by Y" log lines (NPCs, animals and environment ignored). Your server log has not shown any kill or building-damage lines yet, so raid detection only works for keywords you add once you see what the log prints.</p>
    </form>
  );
};

const FlagRow = ({ f, cid, onDismiss }) => (
  <li data-testid={`rule-flag-${f.id}`} className="flex flex-wrap items-start justify-between gap-3 py-3">
    <div className="min-w-0">
      <div className="text-sm font-semibold text-red-400">{f.kind === "pvp_kill" ? `${f.killer} killed ${f.victim}` : `Possible raid · "${f.keyword}"`}</div>
      <div className="mt-0.5 break-all font-mono text-xs text-slate-400">{f.line}</div>
      <div className="mt-0.5 font-mono text-[10px] text-slate-500">{fmtTime(f.created_at)}</div>
    </div>
    <div className="flex items-center gap-2">
      {f.killer && <WarnDialog cid={cid} gamertag={f.killer} defaultReason={`PvP kill during PvE month (${f.victim})`} />}
      <button data-testid={`rule-flag-dismiss-${f.id}`} onClick={() => onDismiss(f.id)} className="flex items-center gap-1 rounded-sm border border-[#1E2D4A] px-2 py-1 text-[11px] uppercase tracking-wider text-slate-300 hover:text-white"><X className="h-3 w-3" />Dismiss</button>
    </div>
  </li>
);

export const RuleWatch = ({ cid, canEdit }) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/communities/${cid}/rule-flags`).then((r) => setData(r.data)).catch(() => setData(null)), [cid]);
  useEffect(() => {
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, [load]);
  const dismiss = async (id) => {
    try { await api.post(`/communities/${cid}/rule-flags/${id}/dismiss`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Card title="PvE rule watch" icon={Eye} testId="rulewatch-card" className="mb-5"
      right={data && <span data-testid="rulewatch-state" className={`font-mono text-xs uppercase tracking-widest ${data.pve_in_effect ? "text-emerald-400" : "text-slate-500"}`}>{data.pve_in_effect ? "Watching · PvE in effect" : "Paused · purge / not PvE"}</span>}>
      <WatchSettings cid={cid} canEdit={canEdit} />
      {!data || data.items.length === 0 ? <EmptyState title="No open flags" testId="rulewatch-empty">Flags appear here, and as staff alerts, when the log shows a player kill or a raid keyword during the PvE month.</EmptyState> : (
        <ul data-testid="rulewatch-flags" className="divide-y divide-[#1E2D4A]">{data.items.map((f) => <FlagRow key={f.id} f={f} cid={cid} onDismiss={dismiss} />)}</ul>
      )}
    </Card>
  );
};
